/**
 * ============================================================================
 * [P1-01] Global App Store (Zustand)
 *
 * 전역 상태 관리 Store
 *
 * 기존 script.js의 Object.defineProperty로 관리되던
 * Manager 간의 의존성을 Zustand Store로 통합
 *
 * 구조:
 * - EditorState: 에디터 관련 상태
 * - SavedTextsState: 저장된 글 관련 상태
 * - UIState: UI 관련 상태 (탭, 모달 등)
 * - SettingsState: 설정 관련 상태
 *
 * Safety:
 * - selectors 활용하여 불필요한 리렌더링 방지
 * - immer 미들웨어로 immutable 업데이트 지원
 * ============================================================================
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { SavedText, TabType, FilterState } from '@/types'

// ============================================================================
// 타입 정의
// ============================================================================

/**
 * 에디터 상태 타입
 */
interface EditorState {
  // 수정/작성 글 패널
  editContent: string
  editTitle: string
  editTopic: string
  editingEditId: string | null  // 편집 중인 작성글 id (null=신규 작성 / 값=기존 글 수정)

  // 레퍼런스 글 패널
  refContent: string
  refTitle: string
  refSource: string
  refType: 'structure' | 'idea' | 'unspecified'
  editingRefId: string | null  // 편집 중인 레퍼런스 id (null=신규 / 값=기존 수정)

  // 공통 설정
  charLimit: 500 | 1000
  isSaving: boolean
  isDirty: boolean

  // SNS 플랫폼 선택
  selectedPlatforms: string[]
}

/**
 * 저장된 글 상태 타입
 */
interface SavedTextsState {
  texts: SavedText[]
  filter: FilterState
  isLoading: boolean
}

/**
 * UI 상태 타입
 */
interface UIState {
  currentTab: TabType
  isModalOpen: boolean
  modalType: string | null
  expandMode: boolean
  expandSourceMode: 'edit' | 'reference' | null
  toastMessage: string | null
  toastType: 'success' | 'error' | 'info' | 'warning' | null
}

/**
 * 설정 상태 타입
 */
interface SettingsState {
  darkMode: boolean
  fontSize: number
  autoSave: boolean
  autoSaveInterval: number
}

/**
 * 레퍼런스 상태 타입
 */
interface ReferenceState {
  selectedReferences: SavedText[]
  recentReferences: SavedText[]
}

/**
 * 트래킹 상태 타입 (간소화)
 */
interface TrackingState {
  isLoading: boolean
  chartMode: 'single' | 'compare'
  chartRange: '7d' | '30d' | '90d' | 'all'
}

// ============================================================================
// 전체 Store 상태 타입
// ============================================================================
interface AppState {
  // 상태
  editor: EditorState
  savedTexts: SavedTextsState
  ui: UIState
  settings: SettingsState
  reference: ReferenceState
  tracking: TrackingState

  // 에디터 액션
  setEditContent: (content: string) => void
  setEditTitle: (title: string) => void
  setEditTopic: (topic: string) => void
  setRefContent: (content: string) => void
  setRefSource: (source: string) => void
  setRefType: (type: 'structure' | 'idea' | 'unspecified') => void
  setCharLimit: (limit: 500 | 1000) => void
  setIsSaving: (saving: boolean) => void
  setSelectedPlatforms: (platforms: string[]) => void
  togglePlatform: (platformId: string) => void
  clearEditor: (panel: 'edit' | 'reference' | 'both') => void
  loadSavedTextToEditor: (text: SavedText) => void

  // 저장된 글 액션
  setSavedTexts: (texts: SavedText[]) => void
  addSavedText: (text: SavedText) => void
  updateSavedText: (id: string, updates: Partial<SavedText>) => void
  deleteSavedText: (id: string) => void
  setSavedTextsFilter: (filter: Partial<FilterState>) => void
  setSavedTextsLoading: (loading: boolean) => void

  // UI 액션
  setCurrentTab: (tab: TabType) => void
  openModal: (type: string) => void
  closeModal: () => void
  setExpandMode: (expand: boolean, source?: 'edit' | 'reference') => void
  showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void
  hideToast: () => void

  // 설정 액션
  toggleDarkMode: () => void
  setFontSize: (size: number) => void
  setAutoSave: (enabled: boolean) => void

  // 레퍼런스 액션
  setSelectedReferences: (refs: SavedText[]) => void
  addSelectedReference: (ref: SavedText) => void
  removeSelectedReference: (id: string) => void
  clearSelectedReferences: () => void

  // 트래킹 액션
  setChartMode: (mode: 'single' | 'compare') => void
  setChartRange: (range: '7d' | '30d' | '90d' | 'all') => void

  // 전체 리셋
  resetStore: () => void
}

// ============================================================================
// 초기 상태
// ============================================================================
const initialEditorState: EditorState = {
  editContent: '',
  editTitle: '',
  editTopic: '',
  editingEditId: null,
  refContent: '',
  refTitle: '',
  refSource: '',
  refType: 'unspecified',
  editingRefId: null,
  charLimit: 500,
  isSaving: false,
  isDirty: false,
  selectedPlatforms: [],
}

const initialSavedTextsState: SavedTextsState = {
  texts: [],
  filter: {
    search: '',
    type: 'all',
    sortBy: 'newest',
  },
  isLoading: false,
}

const initialUIState: UIState = {
  currentTab: 'writing',
  isModalOpen: false,
  modalType: null,
  expandMode: false,
  expandSourceMode: null,
  toastMessage: null,
  toastType: null,
}

const initialSettingsState: SettingsState = {
  darkMode: false,
  fontSize: 16,
  autoSave: true,
  autoSaveInterval: 5000,
}

const initialReferenceState: ReferenceState = {
  selectedReferences: [],
  recentReferences: [],
}

const initialTrackingState: TrackingState = {
  isLoading: false,
  chartMode: 'single',
  chartRange: '7d',
}

// ============================================================================
// [P1-01] Zustand Store 생성
// ============================================================================
export const useAppStore = create<AppState>()(
  devtools(
    persist(
      (set) => ({
        // ====================================================================
        // 초기 상태
        // ====================================================================
        editor: initialEditorState,
        savedTexts: initialSavedTextsState,
        ui: initialUIState,
        settings: initialSettingsState,
        reference: initialReferenceState,
        tracking: initialTrackingState,

        // ====================================================================
        // 에디터 액션
        // ====================================================================
        setEditContent: (content) =>
          set(
            (state) => ({
              editor: { ...state.editor, editContent: content, isDirty: true },
            }),
            false,
            'editor/setEditContent'
          ),

        setEditTitle: (title) =>
          set(
            (state) => ({
              editor: { ...state.editor, editTitle: title },
            }),
            false,
            'editor/setEditTitle'
          ),

        setEditTopic: (topic) =>
          set(
            (state) => ({
              editor: { ...state.editor, editTopic: topic },
            }),
            false,
            'editor/setEditTopic'
          ),

        setRefContent: (content) =>
          set(
            (state) => ({
              editor: { ...state.editor, refContent: content },
            }),
            false,
            'editor/setRefContent'
          ),

        setRefSource: (source) =>
          set(
            (state) => ({
              editor: { ...state.editor, refSource: source },
            }),
            false,
            'editor/setRefSource'
          ),

        setRefType: (type) =>
          set(
            (state) => ({
              editor: { ...state.editor, refType: type },
            }),
            false,
            'editor/setRefType'
          ),

        setCharLimit: (limit) =>
          set(
            (state) => ({
              editor: { ...state.editor, charLimit: limit },
            }),
            false,
            'editor/setCharLimit'
          ),

        setIsSaving: (saving) =>
          set(
            (state) => ({
              editor: { ...state.editor, isSaving: saving },
            }),
            false,
            'editor/setIsSaving'
          ),

        setSelectedPlatforms: (platforms) =>
          set(
            (state) => ({
              editor: { ...state.editor, selectedPlatforms: platforms },
            }),
            false,
            'editor/setSelectedPlatforms'
          ),

        togglePlatform: (platformId) =>
          set(
            (state) => {
              const current = state.editor.selectedPlatforms
              const newPlatforms = current.includes(platformId)
                ? current.filter((id) => id !== platformId)
                : [...current, platformId]
              return {
                editor: { ...state.editor, selectedPlatforms: newPlatforms },
              }
            },
            false,
            'editor/togglePlatform'
          ),

        clearEditor: (panel) =>
          set(
            (state) => {
              if (panel === 'edit') {
                return {
                  editor: {
                    ...state.editor,
                    editContent: '',
                    editTitle: '',
                    editTopic: '',
                    editingEditId: null,
                    isDirty: false,
                  },
                }
              }
              if (panel === 'reference') {
                return {
                  editor: {
                    ...state.editor,
                    refContent: '',
                    refTitle: '',
                    refSource: '',
                    refType: 'unspecified',
                    editingRefId: null,
                  },
                }
              }
              // both
              return {
                editor: {
                  ...initialEditorState,
                  charLimit: state.editor.charLimit,
                },
              }
            },
            false,
            'editor/clearEditor'
          ),

        loadSavedTextToEditor: (text) =>
          set(
            (state) => {
              if (text.type === 'reference') {
                return {
                  editor: {
                    ...state.editor,
                    refContent: text.content,
                    refTitle: text.title || '',
                    refSource: text.source || '',
                    refType: text.referenceType || 'unspecified',
                    editingRefId: text.id,
                  },
                  ui: { ...state.ui, currentTab: 'writing' as TabType },
                }
              }
              // edit 또는 기타 타입
              return {
                editor: {
                  ...state.editor,
                  editContent: text.content,
                  editTitle: text.title || '',
                  editTopic: text.topic || '',
                  editingEditId: text.id,
                },
                ui: { ...state.ui, currentTab: 'writing' as TabType },
              }
            },
            false,
            'editor/loadSavedTextToEditor'
          ),

        // ====================================================================
        // 저장된 글 액션
        // ====================================================================
        setSavedTexts: (texts) =>
          set(
            (state) => ({
              savedTexts: { ...state.savedTexts, texts },
            }),
            false,
            'savedTexts/setSavedTexts'
          ),

        addSavedText: (text) =>
          set(
            (state) => ({
              savedTexts: {
                ...state.savedTexts,
                texts: [text, ...state.savedTexts.texts],
              },
            }),
            false,
            'savedTexts/addSavedText'
          ),

        updateSavedText: (id, updates) =>
          set(
            (state) => ({
              savedTexts: {
                ...state.savedTexts,
                texts: state.savedTexts.texts.map((t) =>
                  t.id === id ? { ...t, ...updates } : t
                ),
              },
            }),
            false,
            'savedTexts/updateSavedText'
          ),

        deleteSavedText: (id) =>
          set(
            (state) => ({
              savedTexts: {
                ...state.savedTexts,
                texts: state.savedTexts.texts.filter((t) => t.id !== id),
              },
            }),
            false,
            'savedTexts/deleteSavedText'
          ),

        setSavedTextsFilter: (filter) =>
          set(
            (state) => ({
              savedTexts: {
                ...state.savedTexts,
                filter: { ...state.savedTexts.filter, ...filter },
              },
            }),
            false,
            'savedTexts/setSavedTextsFilter'
          ),

        setSavedTextsLoading: (loading) =>
          set(
            (state) => ({
              savedTexts: { ...state.savedTexts, isLoading: loading },
            }),
            false,
            'savedTexts/setSavedTextsLoading'
          ),

        // ====================================================================
        // UI 액션
        // ====================================================================
        setCurrentTab: (tab) =>
          set(
            (state) => ({
              ui: { ...state.ui, currentTab: tab },
            }),
            false,
            'ui/setCurrentTab'
          ),

        openModal: (type) =>
          set(
            (state) => ({
              ui: { ...state.ui, isModalOpen: true, modalType: type },
            }),
            false,
            'ui/openModal'
          ),

        closeModal: () =>
          set(
            (state) => ({
              ui: { ...state.ui, isModalOpen: false, modalType: null },
            }),
            false,
            'ui/closeModal'
          ),

        setExpandMode: (expand, source) =>
          set(
            (state) => ({
              ui: {
                ...state.ui,
                expandMode: expand,
                expandSourceMode: expand ? source || null : null,
              },
            }),
            false,
            'ui/setExpandMode'
          ),

        showToast: (message, type) =>
          set(
            (state) => ({
              ui: { ...state.ui, toastMessage: message, toastType: type },
            }),
            false,
            'ui/showToast'
          ),

        hideToast: () =>
          set(
            (state) => ({
              ui: { ...state.ui, toastMessage: null, toastType: null },
            }),
            false,
            'ui/hideToast'
          ),

        // ====================================================================
        // 설정 액션
        // ====================================================================
        toggleDarkMode: () =>
          set(
            (state) => ({
              settings: { ...state.settings, darkMode: !state.settings.darkMode },
            }),
            false,
            'settings/toggleDarkMode'
          ),

        setFontSize: (size) =>
          set(
            (state) => ({
              settings: { ...state.settings, fontSize: size },
            }),
            false,
            'settings/setFontSize'
          ),

        setAutoSave: (enabled) =>
          set(
            (state) => ({
              settings: { ...state.settings, autoSave: enabled },
            }),
            false,
            'settings/setAutoSave'
          ),

        // ====================================================================
        // 레퍼런스 액션
        // ====================================================================
        setSelectedReferences: (refs) =>
          set(
            (state) => ({
              reference: { ...state.reference, selectedReferences: refs },
            }),
            false,
            'reference/setSelectedReferences'
          ),

        addSelectedReference: (ref) =>
          set(
            (state) => {
              // 최대 3개까지만 허용
              const current = state.reference.selectedReferences
              if (current.length >= 3) {
                console.warn('[useAppStore] 최대 3개의 레퍼런스만 선택 가능')
                return state
              }
              // 중복 체크
              if (current.some((r) => r.id === ref.id)) {
                return state
              }
              return {
                reference: {
                  ...state.reference,
                  selectedReferences: [...current, ref],
                },
              }
            },
            false,
            'reference/addSelectedReference'
          ),

        removeSelectedReference: (id) =>
          set(
            (state) => ({
              reference: {
                ...state.reference,
                selectedReferences: state.reference.selectedReferences.filter(
                  (r) => r.id !== id
                ),
              },
            }),
            false,
            'reference/removeSelectedReference'
          ),

        clearSelectedReferences: () =>
          set(
            (state) => ({
              reference: { ...state.reference, selectedReferences: [] },
            }),
            false,
            'reference/clearSelectedReferences'
          ),

        // ====================================================================
        // 트래킹 액션
        // ====================================================================
        setChartMode: (mode) =>
          set(
            (state) => ({
              tracking: { ...state.tracking, chartMode: mode },
            }),
            false,
            'tracking/setChartMode'
          ),

        setChartRange: (range) =>
          set(
            (state) => ({
              tracking: { ...state.tracking, chartRange: range },
            }),
            false,
            'tracking/setChartRange'
          ),

        // ====================================================================
        // 전체 리셋
        // ====================================================================
        resetStore: () =>
          set(
            {
              editor: initialEditorState,
              savedTexts: initialSavedTextsState,
              ui: initialUIState,
              settings: initialSettingsState,
              reference: initialReferenceState,
              tracking: initialTrackingState,
            },
            false,
            'resetStore'
          ),
      }),
      {
        name: 'app-store', // localStorage 키
        partialize: (state) => ({
          // 영속화할 상태 선택
          // [비용 절약] 에디터 내용을 LocalStorage에 저장하여 서버 비용 절감 및 임시 저장 구현
          settings: state.settings,
          ui: { currentTab: state.ui.currentTab },
          editor: {
            charLimit: state.editor.charLimit,
            editContent: state.editor.editContent,
            editTopic: state.editor.editTopic,
            editingEditId: state.editor.editingEditId,
            refContent: state.editor.refContent,
            refSource: state.editor.refSource,
            refType: state.editor.refType,
            editingRefId: state.editor.editingRefId,
            // isSaving 등은 제외
          },
        }),
      }
    ),
    { name: 'AppStore' } // DevTools 이름
  )
)

// ============================================================================
// [Safety] Selectors - 불필요한 리렌더링 방지
// ============================================================================

// 에디터 상태 selectors
export const useEditContent = () => useAppStore((state) => state.editor.editContent)
export const useEditTopic = () => useAppStore((state) => state.editor.editTopic)
export const useRefContent = () => useAppStore((state) => state.editor.refContent)
export const useCharLimit = () => useAppStore((state) => state.editor.charLimit)
export const useIsSaving = () => useAppStore((state) => state.editor.isSaving)
export const useSelectedPlatforms = () => useAppStore((state) => state.editor.selectedPlatforms)

// 저장된 글 selectors
export const useSavedTexts = () => useAppStore((state) => state.savedTexts.texts)
export const useSavedTextsFilter = () => useAppStore((state) => state.savedTexts.filter)
export const useSavedTextsLoading = () => useAppStore((state) => state.savedTexts.isLoading)

// UI selectors
export const useCurrentTab = () => useAppStore((state) => state.ui.currentTab)
export const useIsModalOpen = () => useAppStore((state) => state.ui.isModalOpen)
export const useModalType = () => useAppStore((state) => state.ui.modalType)
export const useExpandMode = () => useAppStore((state) => state.ui.expandMode)
export const useToast = () => useAppStore((state) => ({
  message: state.ui.toastMessage,
  type: state.ui.toastType,
}))

// 설정 selectors
export const useDarkMode = () => useAppStore((state) => state.settings.darkMode)
export const useFontSize = () => useAppStore((state) => state.settings.fontSize)
export const useAutoSave = () => useAppStore((state) => state.settings.autoSave)

// 레퍼런스 selectors
export const useSelectedReferences = () => useAppStore((state) => state.reference.selectedReferences)

// 트래킹 selectors
export const useChartMode = () => useAppStore((state) => state.tracking.chartMode)
export const useChartRange = () => useAppStore((state) => state.tracking.chartRange)

// ============================================================================
// 기본 export
// ============================================================================
export default useAppStore
